<template>
  <div class="ai-alan" :class="{ 'ai-alan--html': mod === 'html' }">
    <slot />

    <div v-if="uretiliyor" class="ai-alan-ortu">
      <i class="pi pi-spinner pi-spin"></i>
    </div>

    <div v-if="gorunur && !disabled" class="ai-alan-kontrol">
      <button
        v-if="oncekiDeger !== null && !uretiliyor"
        type="button"
        class="ai-alan-btn"
        aria-label="Geri al"
        v-tooltip.top="'Geri al'"
        @click="geriAl"
      >
        <i class="pi pi-undo"></i>
      </button>
      <button
        type="button"
        class="ai-alan-btn ai-alan-btn--ana"
        aria-label="AI yardımcı"
        v-tooltip.top="'AI ile yaz / düzelt'"
        :disabled="uretiliyor"
        @click="menuAc"
      >
        <i class="pi pi-sparkles"></i>
      </button>
    </div>

    <Menu v-if="gorunur" ref="menu" :model="menuOgeleri" popup />

    <Popover v-if="gorunur" ref="girdiPanel">
      <div class="flex flex-col gap-2 w-72">
        <template v-if="panelIslem === 'metin_yaz'">
          <label class="text-sm font-semibold" for="ai-alan-istek">Ne yazılsın?</label>
          <Textarea
            id="ai-alan-istek"
            v-model="istek"
            rows="3"
            class="w-full"
            :placeholder="'Örn. ' + (alan || 'bu alan') + ' için kısa bir metin'"
            @keydown.enter.exact.prevent="panelUret"
          />
        </template>
        <template v-else>
          <label class="text-sm font-semibold" for="ai-alan-dil">Hangi dile çevrilsin?</label>
          <InputText id="ai-alan-dil" v-model="hedefDil" class="w-full" placeholder="İngilizce" @keydown.enter.prevent="panelUret" />
        </template>
        <div class="flex justify-end">
          <Button severity="success" :disabled="panelIslem === 'metin_yaz' && !istek.trim()" @click="panelUret">
            <i class="pi pi-sparkles mr-1"></i>{{ panelIslem === "metin_yaz" ? "Yaz" : "Çevir" }}
          </Button>
        </div>
      </div>
    </Popover>
  </div>
</template>

<script>
// AY-1 (V1): form alanlarına genel AI metin yardımcısı. Alanı saran slot bileşeni — mount eden sayfa
// yalnız aynı v-model'i buraya da verir; ✨ menüsü sonucu update:modelValue ile alana yazar, tek
// seviye "Geri al" tutar. Kodlar BE AiYardimciHelper metin_* (lisans/alan/bağlam yok, aipanel yeter).
import { aiAlanYardimciAktif } from "@/services/aiYardimci";

const TONLAR = [
  { kod: "samimi", etiket: "Samimi" },
  { kod: "resmi", etiket: "Resmi" },
  { kod: "kisa", etiket: "Kısa ve net" },
];

export default {
  name: "AiAlanYardimci",
  props: {
    modelValue: { type: String, default: "" },
    // EkGirdi'ye gider — modele metnin nereye yazılacağını anlatır (ör. "Ürün", "Açıklama").
    sayfa: { type: String, default: "" },
    alan: { type: String, default: "" },
    tonSecimi: { type: Boolean, default: false },
    mod: { type: String, default: "text" }, // text | html (editör alanları)
    // Alan salt okunurken (ör. kapalı çağrının çözümü) ✨ gizlenir.
    disabled: { type: Boolean, default: false },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      gorunur: false,
      uretiliyor: false,
      oncekiDeger: null,
      ton: "",
      istek: "",
      hedefDil: "İngilizce",
      panelIslem: "",
    };
  },
  computed: {
    menuOgeleri() {
      const ogeler = [
        { label: "Yaz…", icon: "pi pi-pencil", command: () => this.panelAc("metin_yaz") },
        { label: "Düzelt", icon: "pi pi-check-square", command: () => this.calistir("metin_duzelt") },
        { label: "Kısalt", icon: "pi pi-minus", command: () => this.calistir("metin_kisalt") },
        { label: "Uzat", icon: "pi pi-plus", command: () => this.calistir("metin_uzat") },
        { label: "Özetle", icon: "pi pi-list", command: () => this.calistir("metin_ozetle") },
        { label: "Çevir…", icon: "pi pi-globe", command: () => this.panelAc("metin_cevir") },
      ];
      if (!this.tonSecimi) return ogeler;
      return [
        ...ogeler,
        { separator: true },
        {
          label: "Ton",
          items: TONLAR.map((t) => ({
            label: t.etiket,
            icon: this.ton === t.kod ? "pi pi-check" : "pi pi-circle",
            command: () => { this.ton = this.ton === t.kod ? "" : t.kod; },
          })),
        },
      ];
    },
  },
  async mounted() {
    this.gorunur = await aiAlanYardimciAktif();
  },
  methods: {
    menuAc(e) {
      if (this.uretiliyor) return;
      this._tetikleyici = e.currentTarget;
      this.$refs.menu.toggle(e);
    },
    panelAc(islem) {
      this.panelIslem = islem;
      // Menü komutu kapanırken olay hedefi kaybolur; paneli ✨ düğmesine hizala.
      this.$nextTick(() => this.$refs.girdiPanel.show({ currentTarget: this._tetikleyici }, this._tetikleyici));
    },
    panelUret() {
      if (this.panelIslem === "metin_yaz" && !this.istek.trim()) return;
      this.$refs.girdiPanel.hide();
      this.calistir(this.panelIslem);
    },
    _metin() {
      const v = this.modelValue || "";
      // Editör boşken "<p></p>" / "<p><br></p>" döner — gerçek içerik yoksa boş say.
      if (this.mod === "html" && !v.replace(/<[^>]*>/g, "").trim()) return "";
      return v;
    },
    calistir(kod) {
      if (this.uretiliyor) return;
      const metin = this._metin();
      if (kod !== "metin_yaz" && !metin.trim()) {
        this.$toast.add({ severity: "warn", summary: "Uyarı", detail: "Önce alana bir metin yazın.", life: 3000 });
        return;
      }
      if (metin.length > 8000) {
        this.$toast.add({ severity: "warn", summary: "Uyarı", detail: "Metin çok uzun (en fazla 8000 karakter).", life: 3000 });
        return;
      }
      const girdi = {
        sayfa: this.sayfa,
        alan: this.alan,
        metin,
        istek: kod === "metin_yaz" ? this.istek.trim() : "",
        ton: this.tonSecimi ? this.ton : "",
        hedefDil: kod === "metin_cevir" ? (this.hedefDil.trim() || "İngilizce") : "",
        mod: this.mod === "html" ? "html" : "metin",
      };
      this.uretiliyor = true;
      this.$Servis("POST", "AiYardimci", { Kod: kod, KayitId: 0, EkGirdi: JSON.stringify(girdi) })
        .then((res) => {
          if (res.status === 200 && res.data) {
            const sonuc = this._temizle(res.data.Cikti || "");
            if (!sonuc) {
              this.$toast.add({ severity: "warn", summary: "Uyarı", detail: "Bir metin üretilemedi, tekrar deneyin.", life: 3000 });
              return;
            }
            this.oncekiDeger = this.modelValue || "";
            this.$emit("update:modelValue", sonuc);
            if (kod === "metin_yaz") this.istek = "";
          } else if (res.status === 999) {
            this.$toast.add({ severity: "warn", summary: "Uyarı", detail: res.data, life: 4000 });
          } else {
            this.$toast.add({ severity: "error", summary: "Hata", detail: "Hata oldu", life: 3000 });
          }
        })
        .catch((err) => {
          console.error(err);
          this.$toast.add({ severity: "error", summary: "Hata", detail: "İşlem başarısız", life: 3000 });
        })
        .finally(() => {
          this.uretiliyor = false;
        });
    },
    // Model kurala rağmen ``` çiti ya da tırnak koyarsa temizle.
    _temizle(s) {
      let t = String(s).trim();
      t = t.replace(/^```[a-zA-Z]*\s*\n?/, "").replace(/\n?```\s*$/, "").trim();
      if (t.length > 1 && /^["“].*["”]$/s.test(t)) t = t.slice(1, -1).trim();
      return t;
    },
    geriAl() {
      if (this.oncekiDeger === null) return;
      this.$emit("update:modelValue", this.oncekiDeger);
      this.oncekiDeger = null;
    },
  },
};
</script>

<style scoped>
.ai-alan {
  position: relative;
}
.ai-alan-kontrol {
  position: absolute;
  top: 0.35rem;
  right: 0.35rem;
  display: flex;
  gap: 0.25rem;
  z-index: 2;
}
/* Editörde üst sağ köşe araç çubuğu — kontrol alt sağa iner. */
.ai-alan--html .ai-alan-kontrol {
  top: auto;
  bottom: 2.25rem; /* TinyMCE durum çubuğunun üstü */
}
.ai-alan-btn {
  width: 1.75rem;
  height: 1.75rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  border: 1px solid rgb(209 213 219);
  background: rgba(255, 255, 255, 0.9);
  color: rgb(107 114 128);
  font-size: 0.8rem;
  cursor: pointer;
}
.ai-alan-btn--ana {
  color: rgb(147 51 234);
  border-color: rgb(216 180 254);
}
.ai-alan-btn:hover:not(:disabled) {
  background: rgb(243 232 255);
}
.ai-alan-btn:focus-visible {
  outline: 2px solid rgb(147 51 234);
  outline-offset: 1px;
}
.ai-alan-btn:disabled {
  opacity: 0.5;
  cursor: wait;
}
.dark .ai-alan-btn {
  background: rgba(31, 41, 55, 0.9);
  border-color: rgb(75 85 99);
  color: rgb(209 213 219);
}
.dark .ai-alan-btn--ana {
  color: rgb(216 180 254);
}
.dark .ai-alan-btn:hover:not(:disabled) {
  background: rgb(59 7 100);
}
/* Üretim sürerken alan salt okunur: tıklama/yazma kapanır. */
.ai-alan-ortu {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.55);
  border-radius: 6px;
  cursor: wait;
  z-index: 3;
  color: rgb(147 51 234);
}
.dark .ai-alan-ortu {
  background: rgba(17, 24, 39, 0.55);
}
</style>
